'use client'

import { useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'

interface Post {
  slug: string
  title: string
  description: string
  date: string
  tags: string[]
  image?: string
  readingTime: string
}

export default function BlogList({ posts }: { posts: Post[] }) {
  const [activeTag, setActiveTag] = useState<string | null>(null)

  const allTags = Array.from(new Set(posts.flatMap((p) => p.tags))).sort()

  const filtered = activeTag
    ? posts.filter((p) => p.tags.includes(activeTag))
    : posts

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-12">
        <button
          onClick={() => setActiveTag(null)}
          className={`px-4 py-1.5 text-sm rounded-full border transition-colors ${
            activeTag === null
              ? 'bg-charcoal-900 text-white border-charcoal-900'
              : 'text-charcoal-500 border-charcoal-200 hover:border-charcoal-400'
          }`}
        >
          All
        </button>
        {allTags.map((tag) => (
          <button
            key={tag}
            onClick={() => setActiveTag(tag === activeTag ? null : tag)}
            className={`px-4 py-1.5 text-sm rounded-full border transition-colors ${
              activeTag === tag
                ? 'bg-charcoal-900 text-white border-charcoal-900'
                : 'text-charcoal-500 border-charcoal-200 hover:border-charcoal-400'
            }`}
          >
            {tag}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="text-charcoal-400 text-lg">No posts match this filter.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
          {filtered.map((post) => (
            <article key={post.slug} className="group flex flex-col">
              <Link
                href={`/blog/${post.slug}`}
                className="after:hidden block h-full rounded-2xl bg-[#eeebf3] p-4 md:p-5 transition-colors duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-[#ece8f4]"
              >
                <div className="relative aspect-video overflow-hidden rounded-xl bg-charcoal-100 mb-5">
                  {post.image ? (
                    <Image
                      src={post.image}
                      alt={post.title}
                      fill
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                      className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  ) : null}
                </div>

                <div className="px-1">
                  <div className="flex items-center gap-3 text-xs text-charcoal-500 mb-3">
                    <time dateTime={post.date}>
                      {new Date(post.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </time>
                    <span className="w-1 h-1 rounded-full bg-charcoal-400" />
                    <span>{post.readingTime}</span>
                  </div>

                  <h3 className="text-lg md:text-xl font-semibold text-charcoal-800 group-hover:text-charcoal-600 transition-colors leading-snug mb-2 line-clamp-2">
                    {post.title}
                  </h3>

                  <p className="text-sm text-charcoal-500 leading-relaxed line-clamp-3">
                    {post.description}
                  </p>

                  {post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-4">
                      {post.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center px-2.5 py-1 rounded-full bg-white text-[11px] text-charcoal-600 border border-charcoal-900/[0.06] whitespace-nowrap"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Link>
            </article>
          ))}
        </div>
      )}
    </>
  )
}
